/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-magic-numbers */

import { computed, onMounted, onUnmounted, ref } from 'vue';

export enum Breakpoint {
  mobile = 0, // default
  sm = 40, // 640px
  md = 48, // 768px
  lg = 64, // 1024px
  xl = 80, // 1280px
  '2xl' = 96, // 1536px
}

/**
  Returns the current breakpoint and responsive flags.
  Utilizes mobile first approach, eg. isMinSmall is true if window.innerWidth >= 640px.
 */
export function useResponsive() {
  const breakpoint = ref(getBreakpoint());

  const handleResize = () => {
    breakpoint.value = getBreakpoint();
  };

  onMounted(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
  });

  onUnmounted(() => window.removeEventListener('resize', handleResize));

  return {
    breakpoint,
    /** Viewport < 640px*/
    isMobile: computed(() => getFlags(breakpoint.value).isMobile()),
    /** Viewport >= 640px*/
    isSmall: computed(() => getFlags(breakpoint.value).isSmall()),
    /** Viewport >= 768px*/
    isMedium: computed(() => getFlags(breakpoint.value).isMedium()),
    /** Viewport >= 1024px*/
    isLarge: computed(() => getFlags(breakpoint.value).isLarge()),
    /** Viewport >= 1280px*/
    isExtraLarge: computed(() => getFlags(breakpoint.value).isExtraLarge()),
    /** Viewport >= 1536px*/
    is2XL: computed(() => getFlags(breakpoint.value).is2XL()),
  };
}

export function getBreakpoint() {
  const breakpoints = (
    Object.entries(Breakpoint) as [keyof typeof Breakpoint, number][]
  ).reverse();

  const remToPixelRatio = parseFloat(
    getComputedStyle(document.documentElement).fontSize,
  );

  const name =
    breakpoints.find(
      ([_name, value]) => window.innerWidth >= value * remToPixelRatio,
    )?.[0] ?? 'mobile';

  return Breakpoint[name];
}

export function getFlags(breakpoint: Breakpoint) {
  return {
    isMobile: () => breakpoint < Breakpoint.sm,
    isSmall: () => breakpoint >= Breakpoint.sm,
    isMedium: () => breakpoint >= Breakpoint.md,
    isLarge: () => breakpoint >= Breakpoint.lg,
    isExtraLarge: () => breakpoint >= Breakpoint.xl,
    is2XL: () => breakpoint >= Breakpoint['2xl'],
  };
}
